import AssistantActionCard from "./AssistantActionCard";
import { activeLangCode, assistantDirection, translateAssistantLabel, translateAssistantText } from "./assistantLocalization";
import { hasAiAssistantAccess } from "@/helpers/aiAssistantPermission";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/helpers/api";
import config from "@/config";
import { useUser } from "@/helpers/userContext";
import { useSocket } from "@/helpers/socket";
import { getModuleNamesWithReadAccess } from "@/helpers/permissionResolver";
import { useLang } from "@/helpers/language";
import AudioPlayerWithWaveform from "@/helpers/AudioPlayer";
import LiveRecordingWaveform from "@/components/modules/LiveRecordingWaveform";
import useVoiceRecording from "@/hooks/useVoiceRecording";
import {
	ActionIcon,
	Avatar,
	Badge,
	Box,
	Button,
	Card,
	Collapse,
	Group,
	Loader,
	Menu,
	Modal,
	Paper,
	ScrollArea,
	Stack,
	Table,
	Text,
	TextInput,
	Textarea,
	Tooltip,
	useMantineColorScheme
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconArrowLeft,
	IconBolt,
	IconCheck,
	IconChevronDown,
	IconChevronRight,
	IconDotsVertical,
	IconListDetails,
	IconMenu2,
	IconMessageCircle,
	IconMicrophone,
	IconPaperclip,
	IconPencil,
	IconPin,
	IconPinned,
	IconPinnedOff,
	IconPlayerPauseFilled,
	IconPlus,
	IconSearch,
	IconSend,
	IconSettings,
	IconShieldLock,
	IconSparkles,
	IconTicket,
	IconTrash,
	IconUsers,
	IconWand,
	IconX
} from "@tabler/icons-react";
import {
	getMessageText,
	isMessageTextRepresentedByBlocks,
	compactAssistantBlocks
} from "./assistantMessageUtils";
import {
	assistantUiEnglish,
	assistantUiText
} from "./assistantUiTranslations";
import AssistantMarkdown from "./AssistantMarkdown";
import drawerClasses from "./assistantDrawer.module.css";
import classes from "./aiCommandCenter.module.css";

const TRANSCRIBE_TIMEOUT_MS = Math.max(
	Number(process.env.REACT_APP_TRANSCRIBE_TIMEOUT_MS) || 180000,
	130000
);

const assistantApi = config.api.ai?.assistant || {
	status: "ai/status",
	message: "ai/assistant/message",
	overview: "ai/assistant/overview",
	sessions: "ai/assistant/sessions",
	session: (id) => `ai/assistant/sessions/${id}`,
	sessionUpdate: (id) => `ai/assistant/sessions/${id}`,
	sessionDelete: (id) => `ai/assistant/sessions/${id}`,
	adminLogs: "ai/admin/logs",
	approve: (id) => `ai/approvals/${id}/approve`,
	reject: (id) => `ai/approvals/${id}/reject`,
	transcribeStatus: "ai/assistant/transcribe/status",
	transcribe: "ai/assistant/transcribe"
};

function transcribeStatusUrl() {
	if (assistantApi.transcribeStatus) return assistantApi.transcribeStatus;
	const transcribeUrl = assistantApi.transcribe || "ai/assistant/transcribe";
	return `${transcribeUrl.replace(/\/$/, "")}/status`;
}

const DEFAULT_AGENT_NAME = "APIT Assistant";

function getAssistantBadge(status, canUse, lang) {
	if (!canUse) {
		return { color: "gray", label: assistantUiText("locked", lang) };
	}
	const label = status?.agentName && status.agentName !== DEFAULT_AGENT_NAME ? status.agentName : assistantUiText("defaultAssistantName", lang);
	return { color: status?.ready ? "green" : "teal", label };
}

function isDateLikeString(value) {
	if (typeof value !== "string") return false;
	return /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value) || /^\d{4}-\d{2}-\d{2}T/.test(value);
}

function isObjectIdString(value) {
	return typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);
}

function formatObjectId(value) {
	if (!isObjectIdString(value)) return value;
	return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function formatDateTime(value, lang) {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return new Intl.DateTimeFormat(activeLangCode(lang), {
		year: "numeric",
		month: "short",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit"
	}).format(date);
}

function formatRelativeTime(value, lang) {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	const now = new Date();
	const sameDay = date.toDateString() === now.toDateString();
	if (sameDay) {
		return new Intl.DateTimeFormat(activeLangCode(lang), { hour: "2-digit", minute: "2-digit" }).format(date);
	}
	return new Intl.DateTimeFormat(activeLangCode(lang), { month: "short", day: "2-digit" }).format(date);
}

function isWithinDays(value, days) {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return false;
	return Date.now() - date.getTime() <= days * 86400000;
}

function isSameCalendarDay(value, reference = new Date()) {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return false;
	return date.toDateString() === reference.toDateString();
}

function formatDurationMinutes(value, lang) {
	const minutes = Number(value);
	if (!Number.isFinite(minutes)) return null;
	const formatUnit = (amount, unit) => new Intl.NumberFormat(activeLangCode(lang), {
		style: "unit", unit, unitDisplay: "short"
	}).format(amount);
	if (minutes >= 60 && minutes % 60 === 0) return formatUnit(minutes / 60, "hour");
	if (minutes >= 60) return `${formatUnit(Math.floor(minutes / 60), "hour")} ${formatUnit(minutes % 60, "minute")}`;
	return formatUnit(minutes, "minute");
}

function safeParseJsonString(value) {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	if (!trimmed || !["{", "["].includes(trimmed[0])) return null;
	try {
		return JSON.parse(trimmed);
	} catch (_) {
		return null;
	}
}

function formatObjectValue(value, lang) {
	if (!value || typeof value !== "object") return "-";
	const entries = Object.entries(value).filter(([, itemValue]) => {
		if (itemValue == null || itemValue === "") return false;
		if (Array.isArray(itemValue) && itemValue.length === 0) return false;
		return true;
	});
	if (!entries.length) return "-";
	return entries
		.map(([key, itemValue]) => `${translateAssistantLabel(key, lang)}: ${formatBlockValue(itemValue, lang, key)}`)
		.join("; ");
}

function formatBlockValue(value, lang, label) {
	if (value == null || value === "") return "-";
	if (label === "durationMinutes") {
		const formatted = formatDurationMinutes(value, lang);
		if (formatted) return formatted;
	}
	if (value instanceof Date) return formatDateTime(value, lang) || value.toLocaleString();
	if (isDateLikeString(value)) return formatDateTime(value, lang) || value;
	if (typeof value === "boolean") return translateAssistantLabel(value ? "yes" : "no", lang);
	if (typeof value === "string") {
		const parsed = safeParseJsonString(value);
		if (parsed) return formatBlockValue(parsed, lang, label);
		if (isObjectIdString(value)) return formatObjectId(value);
		if (["domain", "type", "priority", "importance", "status", "coverageType", "riskLevel"].includes(label) || value.includes("_")) {
			return translateAssistantLabel(value, lang);
		}
		return value;
	}
	if (typeof value === "number") return String(value);
	if (Array.isArray(value)) {
		if (!value.length) return "-";
		return value.map((item) => formatBlockValue(item, lang)).join(", ");
	}
	if (typeof value === "object" && (value[activeLangCode(lang)] || value.en)) return value[activeLangCode(lang)] || value.en;
	return formatObjectValue(value, lang);
}

const breakAnywhereStyle = {
	wordBreak: "break-word",
	overflowWrap: "anywhere"
};

const CONTEXT_DEFINITIONS = [
	{ value: "all", labelKey: "contextAll", module: "general" },
	{ value: "page", labelKey: "contextPage", module: "general" },
	{ value: "tickets", labelKey: "contextTickets", module: "ticket", access: ["ticket", "ticketMine", "ticketDashboard", "ticketsDomain_it", "ticketsDomain_hr"] },
	{ value: "attendance", labelKey: "contextAttendance", module: "hr", access: ["attendanceDashboard", "attendanceLog"] },
	{ value: "projects", labelKey: "contextProjects", module: "project", access: ["project", "project-search", "projectDashboard", "todoTask"] },
	{ value: "hr", labelKey: "contextHr", module: "hr", access: ["hrDashboard", "user", "userAll", "leave"] },
	{ value: "crm", labelKey: "contextCrm", module: "crm", access: ["crmDashboard", "contact", "client"] },
	{ value: "it", labelKey: "contextIt", module: "it", access: ["itDashboard", "pc", "laptop", "printer", "software", "room"] },
	{ value: "knowledge", labelKey: "contextKnowledge", module: "general", access: ["knowledge"] },
];

const CREATE_TYPES = [
	{ value: "ticket", labelKey: "ticket", access: "tickets" },
	{ value: "todo task", labelKey: "todoTask", access: "projects" },
	{ value: "meeting", labelKey: "meeting", access: "calendar" },
	{ value: "reminder", labelKey: "reminder", access: "calendar" },
	{ value: "project", labelKey: "project", access: "projects" },
	{ value: "leave request", labelKey: "leaveRequest", access: "hr" },
	{ value: "purchase request", labelKey: "purchaseRequest" },
	{ value: "CRM follow-up", labelKey: "crmFollowUp", access: "crm" },
];

const ANALYZE_PROMPT_KEYS = [
	"promptAttendanceProblems",
	"promptProjectBlockers",
	"quickPromptCompareMonths",
	"quickPromptAnalyzeFile",
];

const TRY_ASKING_PROMPT_KEYS = [
	"quickPromptCompanyToday",
	"quickPromptCriticalTickets",
	"quickPromptAnalyzeFile",
	"quickPromptMeetingReport",
	"quickPromptOfflineDevices",
	"quickPromptCompareMonths",
];

function readMetric(metrics, key) {
	const metric = metrics?.[key];
	if (!metric?.available || !Number.isFinite(Number(metric.count))) return null;
	return Number(metric.count);
}

function contextForModule(moduleName) {
	return CONTEXT_DEFINITIONS.find((context) => context.module === moduleName && context.value !== "page")?.value || "all";
}

function sessionIconFor(moduleName) {
	switch (moduleName) {
		case "ticket": return IconTicket;
		case "hr": return IconUsers;
		case "project":
		case "todo": return IconListDetails;
		case "crm": return IconUsers;
		case "it":
		case "security": return IconShieldLock;
		default: return IconSparkles;
	}
}

function greetingKeyForHour(hour) {
	if (hour < 12) return "greetingMorning";
	if (hour < 18) return "greetingAfternoon";
	return "greetingEvening";
}

function userDisplayName(user, lang) {
	const code = activeLangCode(lang);
	const firstName = user?.firstName?.[code] || user?.firstName?.en;
	if (firstName) return firstName;
	return user?.username || "";
}

function FieldRow({ label, value, lang }) {
	return (
		<Box
			style={{
				display: "flex",
				gap: 8,
				alignItems: "flex-start",
				justifyContent: "space-between",
				width: "100%",
				minWidth: 0,
				flexWrap: "wrap"
			}}
		>
			<Text size="xs" color="dimmed" style={{ flexShrink: 0, maxWidth: "45%", ...breakAnywhereStyle }}>
				{translateAssistantLabel(label, lang)}
			</Text>
			<Text
				size="xs"
				align="right"
				style={{ flex: 1, minWidth: 0, textAlign: "right", ...breakAnywhereStyle }}
			>
				{formatBlockValue(value, lang, label)}
			</Text>
		</Box>
	);
}

function StructuredBlock({ block, onApprove, onReject, busy, resolvedApprovalIds, lang }) {
	if (!block || !block.type) return null;
	if (block.type === "text") {
		return <Text size="sm" style={{ whiteSpace: "pre-wrap", ...breakAnywhereStyle }}>{translateAssistantText(block.text, lang)}</Text>;
	}
	if (block.type === "table") {
		return (
			<Box mt={6} style={{ maxWidth: "100%", minWidth: 0 }}>
				{block.title && <Text size="xs" weight={700} mb={4}>{translateAssistantLabel(block.title, lang)}</Text>}
				<ScrollArea type="auto" offsetScrollbars>
					<Table fontSize="xs" striped highlightOnHover withBorder withColumnBorders style={{ minWidth: 420 }}>
						<thead>
							<tr>
								{(block.columns || []).map((column) => (
									<th key={column.key}>{translateAssistantLabel(column.label || column.key, lang)}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{(block.rows || []).map((row, index) => (
								<tr key={row._id || index}>
									{(block.columns || []).map((column) => (
										<td key={column.key} style={breakAnywhereStyle}>{formatBlockValue(row?.[column.key], lang, column.key)}</td>
									))}
								</tr>
							))}
						</tbody>
					</Table>
				</ScrollArea>
			</Box>
		);
	}
	if (["approval", "card", "confirmation", "failure"].includes(block.type)) {
		return <AssistantActionCard block={block} lang={lang} formatValue={formatBlockValue}
			onApprove={onApprove} onReject={onReject} busy={busy} resolvedApprovalIds={resolvedApprovalIds} />;
	}
	if (block.type === "analytics") {
		return (
			<Card withBorder radius={8} p="sm" mt={6} style={{ maxWidth: "100%", minWidth: 0 }}>
				<Text size="sm" weight={700} style={breakAnywhereStyle}>{translateAssistantLabel(block.title || "analytics", lang)}</Text>
				{block.summary && <Text size="xs" color="dimmed" style={breakAnywhereStyle}>{translateAssistantText(block.summary, lang)}</Text>}
				{(block.metrics || []).map((metric) => (
					<FieldRow key={metric.label} label={metric.label} value={metric.value} lang={lang} />
				))}
			</Card>
		);
	}
	return null;
}

function hasAssistantActivity(activity) {
	return Boolean(
		activity &&
		(activity.summary || activity.emptyReply || (Array.isArray(activity.steps) && activity.steps.length > 0))
	);
}

function AssistantActivity({ activity, lang }) {
	const [opened, setOpened] = useState(false);
	if (!hasAssistantActivity(activity)) return null;

	const steps = Array.isArray(activity.steps) ? activity.steps.filter(Boolean).slice(0, 8) : [];
	return (
		<Box mt={6} style={{ maxWidth: "100%", minWidth: 0 }}>
			<Button
				size="xs"
				compact
				variant="subtle"
				leftIcon={<IconListDetails size={14} />}
				rightIcon={opened ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
				onClick={() => setOpened((value) => !value)}
			>
				{assistantUiText("reasoningSummary", lang)}
			</Button>
			<Collapse in={opened}>
				<Box
					mt={4}
					p="xs"
					style={{
						border: "1px solid var(--mantine-color-dark-4)",
						borderRadius: 8,
						maxWidth: "100%",
						minWidth: 0
					}}
				>
					{activity.summary && (
						<Text size="xs" color="dimmed" style={{ whiteSpace: "pre-wrap", ...breakAnywhereStyle }}>
							{activity.summary}
						</Text>
					)}
					{steps.length > 0 && (
						<Stack spacing={3} mt={activity.summary ? 6 : 0}>
							{steps.map((step, index) => (
								<Box
									key={`${step}-${index}`}
									style={{ display: "flex", gap: 6, alignItems: "flex-start", minWidth: 0 }}
								>
									<Text size="xs" color="dimmed" style={{ flexShrink: 0 }}>{index + 1}.</Text>
									<Text
										size="xs"
										color="dimmed"
										style={{ whiteSpace: "pre-wrap", flex: 1, minWidth: 0, ...breakAnywhereStyle }}
									>
										{step}
									</Text>
								</Box>
							))}
						</Stack>
					)}
				</Box>
			</Collapse>
		</Box>
	);
}

// A just-recorded prompt plays from its local blob; saved ones from the API's upload path.
function voiceMessageUrl(voice) {
	if (voice?.localUrl) return voice.localUrl;
	if (!voice?.path) return null;
	return `${String(config.api.API_URL).replace(/\/$/, "")}/${String(voice.path).replace(/^\//, "")}`;
}

function MessageBubble({ message, onApprove, onReject, approvalBusy, resolvedApprovalIds, lang, colorScheme, agentName }) {
	const isUser = message.role === "user";
	const isDark = colorScheme === "dark";
	const blocks = !isUser && Array.isArray(message?.metadata?.blocks) ? message.metadata.blocks : [];
	const displayBlocks = compactAssistantBlocks(blocks);
	const messageText = getMessageText(message);
	const showMessageText = Boolean(
		messageText && (isUser || !isMessageTextRepresentedByBlocks(messageText, blocks))
	);
	const activity = message?.metadata?.activity;
	const voiceUrl = isUser ? voiceMessageUrl(message.voice) : null;
	const bubbleLayoutStyle = {
		maxWidth: isUser ? "min(78%, 100%)" : "min(860px, 100%)",
		width: !isUser && blocks.length ? "100%" : undefined,
		minWidth: voiceUrl ? "min(340px, 100%)" : 0,
		...breakAnywhereStyle
	};
	const assistantBubbleStyle = {
		background: isDark ? "rgba(30, 41, 59, 0.72)" : "rgba(255, 255, 255, 0.95)",
		border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(148, 163, 184, 0.22)"}`
	};
	const bubbleContent = (
		<>
			<Group spacing={6} mb={showMessageText || blocks.length || voiceUrl ? 4 : 0} style={{ flexWrap: "wrap" }}>
				{!isUser && <img src="/assets/AI/icon.png" alt="" width={14} height={14} style={{ borderRadius: "50%", objectFit: "cover" }} />}
				<Text
					size="xs"
					weight={700}
					className={isUser ? drawerClasses.userBubbleText : undefined}
					color={isUser ? undefined : "dimmed"}
				>
					{isUser ? assistantUiText("you", lang) : agentName || assistantUiText("defaultAssistantName", lang)}
				</Text>
			</Group>
			{voiceUrl && (
				<Box className={`${drawerClasses.voicePlayer} ${isDark ? drawerClasses.voicePlayerDark : drawerClasses.voicePlayerLight}`}>
					<AudioPlayerWithWaveform
						audioUrl={voiceUrl}
						duration={message.voice.duration}
						title={assistantUiText("you", lang)}
						messageId={message._id}
						date={message.createdAt}
					/>
				</Box>
			)}
			{voiceUrl && message.transcribing && (
				<Group spacing={6} mt={6} className={drawerClasses.userBubbleText}>
					<Loader size={12} color="white" />
					<Text size="xs" className={drawerClasses.userBubbleText}>{assistantUiText("transcribingVoice", lang)}</Text>
				</Group>
			)}
			{showMessageText && isUser ? (
				<Text
					size="sm"
					className={drawerClasses.userBubbleText}
					mt={voiceUrl ? 8 : 0}
					style={{
						whiteSpace: "pre-wrap",
						...breakAnywhereStyle
					}}
				>
					{messageText}
				</Text>
			) : null}
			{showMessageText && !isUser ? (
				<AssistantMarkdown content={translateAssistantText(messageText, lang)} colorScheme={colorScheme} />
			) : null}
			{!isUser && <AssistantActivity activity={activity} lang={lang} />}
			{!isUser && displayBlocks.map((block, index) => (
				<StructuredBlock
					key={`${message._id}-${index}`}
					block={block}
					onApprove={onApprove}
					onReject={onReject}
					busy={approvalBusy}
					resolvedApprovalIds={resolvedApprovalIds}
					lang={lang}
				/>
			))}
		</>
	);
	return (
		<Box
			style={{
				display: "flex",
				justifyContent: isUser ? "flex-end" : "flex-start",
				width: "100%",
				minWidth: 0
			}}
		>
			{isUser ? (
				<Box
					p="md"
					className={drawerClasses.userBubble}
					style={{
						...bubbleLayoutStyle,
						borderRadius: 16
					}}
				>
					{bubbleContent}
				</Box>
			) : (
				<Paper
					p="md"
					radius="lg"
					shadow="xs"
					styles={{
						root: {
							...bubbleLayoutStyle,
							...assistantBubbleStyle
						}
					}}
				>
					{bubbleContent}
				</Paper>
			)}
		</Box>
	);
}

export default function AssistantDrawer({ opened, onClose }) {
	const { user } = useUser();
	const { socket } = useSocket();
	const { lang } = useLang();
	const ui = useCallback((key, variables) => assistantUiText(key, lang, variables), [lang]);
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";
	const isCompact = useMediaQuery("(max-width: 860px)");

	const [input, setInput] = useState("");
	const [messages, setMessages] = useState([]);
	const [sessions, setSessions] = useState([]);
	const [sessionId, setSessionId] = useState(null);
	const [activeSessionTitle, setActiveSessionTitle] = useState(null);
	const [selectedContext, setSelectedContext] = useState("all");
	const [overview, setOverview] = useState({ metrics: {}, approvals: [] });
	const [status, setStatus] = useState(null);
	const [pendingApprovals, setPendingApprovals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [approvalBusy, setApprovalBusy] = useState(false);
	const [resolvedApprovalIds, setResolvedApprovalIds] = useState(() => new Set());
	const [transcriptionStatus, setTranscriptionStatus] = useState({ checking: true, available: false });
	const [search, setSearch] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
	const [renameTarget, setRenameTarget] = useState(null);
	const [renameValue, setRenameValue] = useState("");
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [sessionActionBusy, setSessionActionBusy] = useState(false);

	const viewportRef = useRef(null);
	const sessionsRequestRef = useRef(0);
	const chatResetRef = useRef(0);
	const composerRef = useRef(null);

	const [isTranscribing, setIsTranscribing] = useState(false);
	const voiceUrlsRef = useRef([]);
	const {
		isRecording, isPaused: isRecordingPaused, isPending: isRecordingPending,
		duration: recordingTime, analyser: recordingAnalyser,
		start: startVoiceRecording, stop: stopVoiceRecording, discard: discardRecording,
		togglePause: togglePauseRecording
	} = useVoiceRecording({
		onComplete: ({ file, duration, send }) => {
			if (send) sendVoiceMessage(file, duration);
		},
		onError: (error) => {
			showNotification({
				color: "red",
				message: error?.name === "NotAllowedError"
					? ui("microphoneDenied")
					: ui("microphoneFailed", { error: error?.message || "" })
			});
		}
	});
	const discardRecordingRef = useRef(discardRecording);
	discardRecordingRef.current = discardRecording;

	const canUse = hasAiAssistantAccess(user);
	const readableModules = useMemo(() => getModuleNamesWithReadAccess(user), [user]);
	const contextOptions = useMemo(
		() => CONTEXT_DEFINITIONS
			.filter((context) => !context.access || context.access.some((moduleName) => {
				if (readableModules === null) return true;
				return readableModules.includes(moduleName);
			}))
			.map(({ value, labelKey }) => ({ value, label: ui(labelKey) })),
		[readableModules, ui]
	);
	const activeContext = CONTEXT_DEFINITIONS.find((context) => context.value === selectedContext) || CONTEXT_DEFINITIONS[0];
	const createTypeOptions = useMemo(
		() => CREATE_TYPES.filter((type) => !type.access || contextOptions.some((context) => context.value === type.access)),
		[contextOptions]
	);
	const overviewMetrics = overview?.metrics || {};
	const assistantBadge = getAssistantBadge(status, canUse, lang);
	const greeting = ui(greetingKeyForHour(new Date().getHours()), { name: userDisplayName(user, lang) });

	const scrollToBottom = () => {
		setTimeout(() => {
			viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight, behavior: "smooth" });
		}, 80);
	};

	const markApprovalResolved = useCallback((id) => {
		if (!id) return;
		const approvalId = String(id);
		setResolvedApprovalIds((prev) => {
			if (prev.has(approvalId)) return prev;
			const next = new Set(prev);
			next.add(approvalId);
			return next;
		});
	}, []);

	const loadSessions = useCallback(async (searchTerm = "") => {
		if (!canUse) return;
		const requestId = sessionsRequestRef.current + 1;
		sessionsRequestRef.current = requestId;
		try {
			const res = await apiClient.get(assistantApi.sessions, {
				params: { limit: 100, module: "general", q: searchTerm || undefined }
			});
			if (sessionsRequestRef.current === requestId) {
				setSessions(res?.data?.sessions || []);
			}
		} catch (_) {
			/* history is optional in the window */
		}
	}, [canUse]);

	const loadStatus = useCallback(async () => {
		if (!canUse) return;
		try {
			const res = await apiClient.get(assistantApi.status);
			setStatus(res?.data?.status || null);
		} catch (_) {
			setStatus({ enabled: false, ready: false });
		}
	}, [canUse]);

	const loadTranscriptionStatus = useCallback(async () => {
		if (!canUse) {
			setTranscriptionStatus({ checking: false, available: false });
			return false;
		}
		setTranscriptionStatus((prev) => ({ ...prev, checking: true }));
		try {
			const res = await apiClient.get(transcribeStatusUrl(), {
				timeout: 5000,
				skipAuthFailureHandler: true
			});
			const available = Boolean(res?.data?.available || res?.data?.ok);
			setTranscriptionStatus({
				checking: false,
				available,
				error: available ? null : res?.data?.error || ui("transcriptionUnavailable")
			});
			return available;
		} catch (error) {
			setTranscriptionStatus({
				checking: false,
				available: false,
				error: error?.response?.data?.error || ui("transcriptionUnavailable")
			});
			return false;
		}
	}, [canUse, ui]);

	const loadOverview = useCallback(async () => {
		if (!canUse) return;
		try {
			const res = await apiClient.get(assistantApi.overview || "ai/assistant/overview");
			const nextOverview = {
				metrics: res?.data?.metrics || {},
				approvals: res?.data?.approvals || []
			};
			setOverview(nextOverview);
			setPendingApprovals(nextOverview.approvals);
		} catch (_) {
			/* overview is supplementary */
		}
	}, [canUse]);

	useEffect(() => {
		if (!opened) return;
		loadStatus();
		loadOverview();
		loadTranscriptionStatus();
		const interval = setInterval(loadTranscriptionStatus, 30000);
		return () => clearInterval(interval);
	}, [opened, loadOverview, loadStatus, loadTranscriptionStatus]);

	useEffect(() => {
		if (!opened) return;
		const timer = setTimeout(() => loadSessions(search), search ? 300 : 0);
		return () => clearTimeout(timer);
	}, [opened, search, loadSessions]);

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	useEffect(() => {
		if (!socket || !opened) return undefined;
		const handleAssistantMessage = (payload) => {
			if (!payload?.message) return;
			if (sessionId && String(payload.sessionId) !== String(sessionId)) return;
			setMessages((prev) => {
				if (prev.some((msg) => String(msg._id) === String(payload.message._id))) return prev;
				return [...prev, payload.message];
			});
		};
		const handlePendingApproval = (payload) => {
			if (payload?.approval) {
				setPendingApprovals((prev) =>
					prev.some((approval) => String(approval._id) === String(payload.approval._id))
						? prev
						: [payload.approval, ...prev]
				);
				loadOverview();
			}
		};
		const removeApproval = (payload) => {
			if (payload?.approval?._id) {
				markApprovalResolved(payload.approval._id);
				setPendingApprovals((prev) =>
					prev.filter((approval) => String(approval._id) !== String(payload.approval._id))
				);
				loadOverview();
			}
		};
		socket.on("ai:assistant:message", handleAssistantMessage);
		socket.on("ai:approval:pending", handlePendingApproval);
		socket.on("ai:approval:approved", removeApproval);
		socket.on("ai:approval:rejected", removeApproval);
		return () => {
			socket.off("ai:assistant:message", handleAssistantMessage);
			socket.off("ai:approval:pending", handlePendingApproval);
			socket.off("ai:approval:approved", removeApproval);
			socket.off("ai:approval:rejected", removeApproval);
		};
	}, [socket, opened, sessionId, loadOverview, markApprovalResolved]);

	const focusComposer = useCallback(() => {
		setTimeout(() => composerRef.current?.focus(), 30);
	}, []);

	const seedComposer = useCallback((text) => {
		setInput((prev) => (prev ? prev : text));
		focusComposer();
	}, [focusComposer]);

	const startNewChat = useCallback(() => {
		chatResetRef.current += 1;
		setSessionId(null);
		setActiveSessionTitle(null);
		setMessages([]);
		setInput("");
		setLoading(false);
		setSelectedContext("all");
		if (isCompact) setMobileSidebarOpen(false);
		focusComposer();
	}, [isCompact, focusComposer]);

	const startNewChatRef = useRef(startNewChat);
	startNewChatRef.current = startNewChat;
	useEffect(() => {
		// Every time the assistant is opened it starts a fresh chat; past chats stay in the sidebar.
		if (opened) {
			startNewChatRef.current();
			return;
		}
		// Closing releases the microphone and the local recordings the old chat was playing.
		discardRecordingRef.current();
		voiceUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
		voiceUrlsRef.current = [];
	}, [opened]);

	const loadSession = async (sessionOrId) => {
		const sessionRecord = typeof sessionOrId === "object"
			? sessionOrId
			: sessions.find((session) => String(session._id) === String(sessionOrId));
		const id = sessionRecord?._id || sessionOrId;
		setSessionId(id || null);
		setActiveSessionTitle(sessionRecord?.title || null);
		if (isCompact) setMobileSidebarOpen(false);
		if (!id) {
			setMessages([]);
			return;
		}
		if (sessionRecord?.module) {
			setSelectedContext(contextForModule(sessionRecord.module));
		}
		try {
			const res = await apiClient.get(assistantApi.session(id));
			setMessages(res?.data?.messages || []);
			if (res?.data?.session?.title) setActiveSessionTitle(res.data.session.title);
		} catch (error) {
			showNotification({ color: "red", message: error?.response?.data?.error || ui("failedLoadSession") });
		}
	};

	const startRecording = async () => {
		const available = await loadTranscriptionStatus();
		if (!available) {
			showNotification({ color: "red", message: ui("transcriptionUnavailable") });
			return;
		}
		startVoiceRecording("audio");
	};

	// The user sees a voice message; the AI receives only its transcript.
	const sendVoiceMessage = async (file, duration) => {
		const chatResetVersion = chatResetRef.current;
		const localUrl = URL.createObjectURL(file);
		voiceUrlsRef.current.push(localUrl);
		const optimisticId = `local-voice-${Date.now()}`;
		setMessages((prev) => [...prev, {
			_id: optimisticId,
			role: "user",
			content: "",
			voice: { localUrl, duration },
			transcribing: true,
			createdAt: new Date().toISOString()
		}]);
		setIsTranscribing(true);
		let transcript = "";
		let savedVoice = null;
		let failed = false;
		try {
			const formData = new FormData();
			formData.append("audio", file);
			formData.append("keepAudio", "true");
			formData.append("duration", String(duration || 0));
			const transcribeUrl = assistantApi.transcribe || "ai/assistant/transcribe";
			const res = await apiClient.post(transcribeUrl, formData, {
				headers: { "Content-Type": "multipart/form-data" },
				timeout: TRANSCRIBE_TIMEOUT_MS
			});
			transcript = String(res?.data?.text || "").trim();
			savedVoice = res?.data?.voice || null;
		} catch (err) {
			failed = true;
			if (err?.response?.status === 503) {
				setTranscriptionStatus({
					checking: false,
					available: false,
					error: err?.response?.data?.error || ui("transcriptionUnavailable")
				});
			}
			showNotification({
				color: "red",
				message: ui("transcriptionFailed", { error: err?.response?.data?.error || err.message })
			});
		} finally {
			setIsTranscribing(false);
		}
		if (chatResetVersion !== chatResetRef.current) return;
		if (!transcript) {
			setMessages((prev) => prev.filter((msg) => msg._id !== optimisticId));
			if (!failed) showNotification({ color: "orange", message: ui("transcriptionEmpty") });
			return;
		}
		const voice = { ...(savedVoice || {}), localUrl, duration: savedVoice?.duration || duration };
		setMessages((prev) => prev.map((msg) => (
			msg._id === optimisticId ? { ...msg, content: transcript, voice, transcribing: false } : msg
		)));
		sendMessage(transcript, { optimisticId, voice });
	};

	const currentRoute = typeof window !== "undefined" ? window.location.pathname : "/";

	const sendMessage = async (messageOverride = null, options = {}) => {
		const message = (typeof messageOverride === "string" ? messageOverride : input).trim();
		const displayMessage = String(options.displayMessage || message).trim();
		if (!message || loading || (isTranscribing && !options.optimisticId)) return;
		const requestContext = CONTEXT_DEFINITIONS.find(
			(context) => context.value === (options.contextValue || selectedContext)
		) || CONTEXT_DEFINITIONS[0];
		const requestSessionId = options.newSession ? null : sessionId;
		const chatResetVersion = chatResetRef.current;
		// A voice message is already on screen and leaves whatever is typed in the composer alone.
		if (!options.optimisticId) setInput("");
		setLoading(true);
		const optimistic = {
			_id: options.optimisticId || `local-${Date.now()}`,
			role: "user",
			content: displayMessage,
			...(options.voice ? { voice: options.voice } : {}),
			createdAt: new Date().toISOString()
		};
		if (!options.optimisticId) setMessages((prev) => [...prev, optimistic]);
		const withLocalVoice = (serverUserMessage) => ({
			...serverUserMessage,
			content: getMessageText(serverUserMessage) || optimistic.content,
			...(optimistic.voice ? { voice: { ...(serverUserMessage.voice || {}), localUrl: optimistic.voice.localUrl, duration: serverUserMessage.voice?.duration || optimistic.voice.duration } } : {})
		});
		try {
			const res = await apiClient.post(assistantApi.message, {
				message,
				displayMessage,
				sessionId: requestSessionId,
				module: "general",
				...(options.voice?.upload ? { voice: { upload: options.voice.upload } } : {}),
				route: currentRoute,
				pageContext: {
					selectedRows: [],
					filters: {},
					visibleColumns: [],
					contextScope: requestContext.value,
					contextLabel: assistantUiEnglish(requestContext.labelKey),
					uiLanguage: activeLangCode(lang)
				},
				context: {
					route: currentRoute,
					scope: requestContext.value,
					scopeLabel: assistantUiEnglish(requestContext.labelKey),
					currentPage: requestContext.value === "page",
					uiLanguage: activeLangCode(lang)
				}
			});
			if (chatResetVersion !== chatResetRef.current) return;
			const nextSessionId = res?.data?.session?._id || requestSessionId;
			setSessionId(nextSessionId ? String(nextSessionId) : null);
			if (res?.data?.session?.title) setActiveSessionTitle(res.data.session.title);
			if (res?.data?.message) {
				setMessages((prev) => {
					const withoutOptimistic = prev.filter((msg) => msg._id !== optimistic._id);
					const existingAssistant = withoutOptimistic.find(
						(msg) => String(msg._id) === String(res.data.message._id)
					);
					const serverUserMessage = res?.data?.userMessage;
					const finalUserMessage = serverUserMessage ? withLocalVoice(serverUserMessage) : optimistic;
					return existingAssistant
						? [
							...withoutOptimistic.filter(
								(msg) => String(msg._id) !== String(res.data.message._id)
							),
							finalUserMessage,
							existingAssistant
						]
						: [...withoutOptimistic, finalUserMessage, res.data.message];
				});
			} else if (res?.data?.userMessage) {
				setMessages((prev) => {
					const withoutOptimistic = prev.filter((msg) => msg._id !== optimistic._id);
					const finalUserMessage = withLocalVoice(res.data.userMessage);
					if (withoutOptimistic.some((msg) => String(msg._id) === String(finalUserMessage._id))) {
						return withoutOptimistic.map((msg) =>
							String(msg._id) === String(finalUserMessage._id) ? finalUserMessage : msg
						);
					}
					return [...withoutOptimistic, finalUserMessage];
				});
			}
			loadSessions(search);
		} catch (error) {
			if (chatResetVersion !== chatResetRef.current) return;
			setMessages((prev) => prev.filter((msg) => msg._id !== optimistic._id));
			setInput((prev) => (options.optimisticId && prev ? prev : displayMessage));
			showNotification({
				color: "red",
				message: error?.response?.data?.error || ui("assistantUnavailable")
			});
		} finally {
			if (chatResetVersion === chatResetRef.current) {
				setLoading(false);
			}
		}
	};

	const submitPrompt = (prompt, displayMessage = prompt, contextValue = selectedContext) => {
		if (!prompt) return;
		sendMessage(prompt, { contextValue, displayMessage });
	};

	const approve = async (id) => {
		if (!id) return;
		setApprovalBusy(true);
		try {
			const res = await apiClient.post(assistantApi.approve(id), {});
			markApprovalResolved(id);
			setPendingApprovals((prev) => prev.filter((approval) => String(approval._id) !== String(id)));
			if (res?.data?.message) {
				setMessages((prev) => [...prev, res.data.message]);
			}
			loadOverview();
			showNotification({ color: "green", message: ui("actionApproved") });
		} catch (error) {
			if (error?.response?.status === 409) {
				markApprovalResolved(id);
			}
			showNotification({ color: "red", message: error?.response?.data?.error || ui("approvalFailed") });
		} finally {
			setApprovalBusy(false);
		}
	};

	const reject = async (id) => {
		if (!id) return;
		setApprovalBusy(true);
		try {
			const res = await apiClient.post(assistantApi.reject(id), {});
			markApprovalResolved(id);
			setPendingApprovals((prev) => prev.filter((approval) => String(approval._id) !== String(id)));
			if (res?.data?.message) {
				setMessages((prev) => [...prev, res.data.message]);
			}
			loadOverview();
		} catch (error) {
			if (error?.response?.status === 409) {
				markApprovalResolved(id);
			}
			showNotification({ color: "red", message: error?.response?.data?.error || ui("rejectFailed") });
		} finally {
			setApprovalBusy(false);
		}
	};

	const openRename = (session) => {
		setRenameTarget(session);
		setRenameValue(session.title || "");
	};

	const submitRename = async () => {
		if (!renameTarget) return;
		const title = renameValue.trim();
		if (!title) return;
		setSessionActionBusy(true);
		try {
			const res = await apiClient.patch(assistantApi.sessionUpdate(renameTarget._id), { title });
			const nextTitle = res?.data?.session?.title || title;
			setSessions((prev) => prev.map((s) => (String(s._id) === String(renameTarget._id) ? { ...s, title: nextTitle } : s)));
			if (String(sessionId) === String(renameTarget._id)) setActiveSessionTitle(nextTitle);
			showNotification({ color: "green", message: ui("chatRenamed") });
			setRenameTarget(null);
		} catch (error) {
			showNotification({ color: "red", message: error?.response?.data?.error || ui("failedLoadSession") });
		} finally {
			setSessionActionBusy(false);
		}
	};

	const togglePin = async (session) => {
		try {
			const res = await apiClient.patch(assistantApi.sessionUpdate(session._id), { pinned: !session.pinned });
			const nextPinned = res?.data?.session?.pinned ?? !session.pinned;
			setSessions((prev) => [...prev]
				.map((s) => (String(s._id) === String(session._id) ? { ...s, pinned: nextPinned } : s))
				.sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned))
					|| new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)));
		} catch (error) {
			showNotification({ color: "red", message: error?.response?.data?.error || ui("approvalFailed") });
		}
	};

	const confirmDeleteSession = async () => {
		if (!deleteTarget) return;
		setSessionActionBusy(true);
		try {
			await apiClient.delete(assistantApi.sessionDelete(deleteTarget._id));
			setSessions((prev) => prev.filter((s) => String(s._id) !== String(deleteTarget._id)));
			if (String(sessionId) === String(deleteTarget._id)) startNewChat();
			showNotification({ color: "green", message: ui("chatDeleted") });
			setDeleteTarget(null);
		} catch (error) {
			showNotification({ color: "red", message: error?.response?.data?.error || ui("failedLoadSession") });
		} finally {
			setSessionActionBusy(false);
		}
	};

	const filteredSessions = useMemo(() => {
		if (activeFilter === "pinned") return sessions.filter((s) => s.pinned);
		if (activeFilter === "today") return sessions.filter((s) => isSameCalendarDay(s.updatedAt || s.createdAt));
		if (activeFilter === "week") return sessions.filter((s) => isWithinDays(s.updatedAt || s.createdAt, 7));
		return sessions;
	}, [sessions, activeFilter]);

	const recentSessions = useMemo(
		() => [...sessions]
			.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
			.slice(0, 3),
		[sessions]
	);

	const showHome = !sessionId && messages.length === 0;
	const voiceInputDisabled = loading || isTranscribing || isRecordingPending || !transcriptionStatus.available;
	const voiceInputTooltip = transcriptionStatus.checking
		? ui("checkingTranscription")
		: transcriptionStatus.available
			? ui("voiceInput")
			: ui("transcriptionUnavailable");
	const formatRecordingTime = (seconds) => {
		const total = Math.max(0, Math.floor(seconds || 0));
		return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
	};

	const homeCards = [
		{
			key: "summarize",
			title: ui("cardSummarizeTitle"),
			subtitle: ui("cardSummarizeSubtitle"),
			icon: IconSparkles,
			color: "violet",
			onClick: () => submitPrompt(assistantUiEnglish("promptCompanyActivity"), ui("cardSummarizeTitle"), "all")
		},
		{
			key: "find",
			title: ui("cardFindTitle"),
			subtitle: ui("cardFindSubtitle"),
			icon: IconSearch,
			color: "blue",
			onClick: focusComposer
		},
		{
			key: "create",
			title: ui("cardCreateTitle"),
			subtitle: ui("cardCreateSubtitle"),
			icon: IconPlus,
			color: "teal",
			menu: createTypeOptions
		},
		{
			key: "takeAction",
			title: ui("cardTakeActionTitle"),
			subtitle: ui("cardTakeActionSubtitle"),
			icon: IconBolt,
			color: "orange",
			onClick: focusComposer
		},
		{
			key: "taskKpi",
			title: ui("cardKpiTitle"),
			subtitle: ui("cardKpiSubtitle"),
			icon: IconCheck,
			color: "green",
			badge: readMetric(overviewMetrics, "completedTodos"),
			onClick: () => submitPrompt(
				assistantUiEnglish("promptTaskKpi", { count: readMetric(overviewMetrics, "completedTodos") ?? 0 }),
				ui("cardKpiTitle")
			)
		},
	];

	const barItems = [
		{ key: "myDay", label: ui("myDay"), icon: IconBolt, badge: readMetric(overviewMetrics, "myDay") },
		{ key: "summarize", label: ui("companyActivity"), icon: IconSparkles },
		{ key: "find", label: ui("findAnything"), icon: IconSearch },
		{ key: "create", label: ui("create"), icon: IconPlus, menu: createTypeOptions },
		{ key: "analyze", label: ui("barAnalyze"), icon: IconChevronDown, menu: ANALYZE_PROMPT_KEYS },
		{ key: "takeAction", label: ui("cardTakeActionTitle"), icon: IconWand },
		{ key: "draft", label: ui("barDraft"), icon: IconPencil },
		{ key: "taskKpi", label: ui("cardKpiTitle"), icon: IconCheck, badge: readMetric(overviewMetrics, "completedTodos") },
		{ key: "more", label: ui("more"), icon: IconDotsVertical, kind: "more" },
	];

	const runBarAction = (key) => {
		if (key === "myDay") return submitPrompt(assistantUiEnglish("promptMyDay"), ui("myDay"));
		if (key === "summarize") return submitPrompt(assistantUiEnglish("promptCompanyActivity"), ui("companyActivity"), "all");
		if (key === "find") return focusComposer();
		if (key === "takeAction") return focusComposer();
		if (key === "draft") return seedComposer(`${ui("barDraft")}: `);
		if (key === "taskKpi") return submitPrompt(
			assistantUiEnglish("promptTaskKpi", { count: readMetric(overviewMetrics, "completedTodos") ?? 0 }),
			ui("cardKpiTitle")
		);
		return null;
	};

	if (!opened) return null;

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			dir={assistantDirection(lang)}
			fullScreen
			withCloseButton={false}
			padding={0}
			overlayProps={{ opacity: 0.6, blur: 8 }}
			classNames={{ body: drawerClasses.assistantScope }}
			styles={{
				body: { height: "100dvh", padding: 0, display: "flex", flexDirection: "column", minWidth: 0 },
				content: {
					background: isDark
						? "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(2, 6, 23, 0.98) 100%)"
						: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)"
				}
			}}
		>
			{!canUse ? (
				<Box p="xl">
					<Paper p="md" radius={8} withBorder>
						<Group spacing={8}>
							<IconShieldLock size={18} />
							<Text size="sm">{ui("missingAssistantPermission")}</Text>
						</Group>
					</Paper>
				</Box>
			) : (
				<Box className={classes.shell}>
					{(!isCompact || mobileSidebarOpen) && (
						<Box
							className={`${classes.sidebar} ${isDark ? classes.sidebarDark : classes.sidebarLight}`}
							style={isCompact ? { position: "absolute", zIndex: 20, height: "100%" } : undefined}
						>
							<Box className={classes.sidebarHeader}>
								<Group position="apart" noWrap mb={10}>
									<Group spacing={8} noWrap>
										<Box
											style={{
												width: 32, height: 32, borderRadius: 10, display: "flex",
												alignItems: "center", justifyContent: "center",
												background: "linear-gradient(135deg, rgba(37,99,235,0.22), rgba(124,58,237,0.22))",
												overflow: "hidden"
											}}
										>
											<img src="/assets/AI/icon.png" alt="" width={22} height={22} style={{ objectFit: "cover" }} />
										</Box>
										<Text weight={700} size="sm">{ui("aiAssistant")}</Text>
									</Group>
									<Group spacing={4} noWrap>
										<Tooltip label={ui("newChat")}>
											<ActionIcon variant="light" radius="xl" onClick={startNewChat}>
												<IconPlus size={15} />
											</ActionIcon>
										</Tooltip>
										{isCompact && (
											<ActionIcon variant="subtle" radius="xl" onClick={() => setMobileSidebarOpen(false)}>
												<IconX size={16} />
											</ActionIcon>
										)}
									</Group>
								</Group>
								<TextInput
									value={search}
									onChange={(event) => setSearch(event.currentTarget.value)}
									placeholder={ui("searchChatsPlaceholder")}
									icon={<IconSearch size={14} />}
									radius="xl"
									size="xs"
									mb={8}
								/>
								<Group spacing={6}>
									{[
										{ key: "today", label: ui("filterToday") },
										{ key: "week", label: ui("filterThisWeek") },
										{ key: "pinned", label: ui("filterPinned") },
									].map((filter) => (
										<Button
											key={filter.key}
											size="xs"
											compact
											radius="xl"
											variant={activeFilter === filter.key ? "filled" : "light"}
											color={activeFilter === filter.key ? "blue" : "gray"}
											onClick={() => setActiveFilter((prev) => (prev === filter.key ? "all" : filter.key))}
										>
											{filter.label}
										</Button>
									))}
								</Group>
							</Box>
							<ScrollArea className={classes.sidebarBody}>
								<Text size="xs" color="dimmed" weight={700} className={classes.sidebarSectionLabel}>
									{ui("myChatsHeading")}
								</Text>
								{filteredSessions.length === 0 && (
									<Text size="xs" color="dimmed" px={14} py={6}>{ui("noChatsYet")}</Text>
								)}
								{filteredSessions.map((session) => {
									const SessionIcon = sessionIconFor(session.module);
									const isActive = String(session._id) === String(sessionId);
									return (
										<Box
											key={session._id}
											className={`${classes.chatRow} ${isActive ? classes.chatRowActive : ""}`}
											onClick={() => loadSession(session)}
										>
											<Box className={classes.chatRowIcon}>
												<SessionIcon size={15} color={isDark ? "#93c5fd" : "#2563eb"} />
											</Box>
											<Box className={classes.chatRowBody}>
												<Text size="sm" weight={isActive ? 700 : 500} lineClamp={1}>
													{session.title || ui("newChat")}
												</Text>
												<Text size="xs" color="dimmed" lineClamp={1}>
													{formatRelativeTime(session.updatedAt || session.createdAt, lang)}
												</Text>
											</Box>
											{session.pinned && (
												<IconPinned size={14} color={isDark ? "#93c5fd" : "#2563eb"} style={{ flexShrink: 0 }} />
											)}
											<Menu width={180} shadow="md" position="bottom-end" withinPortal>
												<Menu.Target>
													<ActionIcon
														size="sm"
														variant="subtle"
														className={classes.chatRowMenuBtn}
														onClick={(event) => event.stopPropagation()}
													>
														<IconDotsVertical size={14} />
													</ActionIcon>
												</Menu.Target>
												<Menu.Dropdown onClick={(event) => event.stopPropagation()}>
													<Menu.Item icon={<IconPencil size={14} />} onClick={() => openRename(session)}>
														{ui("renameChat")}
													</Menu.Item>
													<Menu.Item
														icon={session.pinned ? <IconPinnedOff size={14} /> : <IconPin size={14} />}
														onClick={() => togglePin(session)}
													>
														{ui(session.pinned ? "unpinChat" : "pinChat")}
													</Menu.Item>
													<Menu.Item color="red" icon={<IconTrash size={14} />} onClick={() => setDeleteTarget(session)}>
														{ui("deleteChat")}
													</Menu.Item>
												</Menu.Dropdown>
											</Menu>
										</Box>
									);
								})}
							</ScrollArea>
							<Box className={classes.sidebarFooter}>
								<Box className={classes.sidebarFooterRow}>
									<Avatar radius="xl" size={28} src={user?.image || undefined}>
										{(userDisplayName(user, lang) || "?").slice(0, 1).toUpperCase()}
									</Avatar>
									<Text size="sm" weight={600} lineClamp={1} style={{ flex: 1 }}>
										{userDisplayName(user, lang)}
									</Text>
									{user?.ananunaki === true && (
										<Tooltip label={ui("aiSettings")}>
											<ActionIcon component={Link} to="/system/ai-settings" onClick={onClose} variant="subtle" radius="xl">
												<IconSettings size={16} />
											</ActionIcon>
										</Tooltip>
									)}
								</Box>
							</Box>
						</Box>
					)}

					<Box className={classes.main}>
						<Box className={classes.mainHeader}>
							{isCompact && (
								<ActionIcon variant="subtle" radius="xl" onClick={() => setMobileSidebarOpen(true)}>
									<IconMenu2 size={18} />
								</ActionIcon>
							)}
							{!showHome && (
								<ActionIcon variant="subtle" radius="xl" onClick={startNewChat}>
									<IconArrowLeft size={18} />
								</ActionIcon>
							)}
							<Box style={{ flex: 1, minWidth: 0 }}>
								<Text weight={700} size="sm" lineClamp={1}>
									{showHome ? ui("aiAssistant") : (activeSessionTitle || ui("newChat"))}
								</Text>
								{!showHome && activeContext.value !== "all" && (
									<Text size="xs" color="dimmed" lineClamp={1}>{ui(activeContext.labelKey)}</Text>
								)}
							</Box>
							{pendingApprovals.length > 0 && (
								<Tooltip label={ui("needsApproval")}>
									<Badge size="sm" color="orange" variant="light" radius="sm">
										{ui("needsApproval")}: {pendingApprovals.length}
									</Badge>
								</Tooltip>
							)}
							<Badge size="sm" color={assistantBadge.color} variant="light" radius="sm">
								{assistantBadge.label}
							</Badge>
							<ActionIcon variant="subtle" radius="xl" onClick={onClose}>
								<IconX size={18} />
							</ActionIcon>
						</Box>

						<Box className={classes.mainScrollWrap}>
						<ScrollArea className={classes.mainScroll} viewportRef={viewportRef} type="auto" scrollbarSize={6}>
							{showHome ? (
								<Box className={classes.homeWrap}>
									<Box className={classes.homeHeroIcon}>
										<img src="/assets/AI/icon.png" alt="" width={36} height={36} style={{ borderRadius: "50%", objectFit: "cover" }} />
									</Box>
									<Text size={26} weight={800} mt={14}>{greeting}</Text>
									<Text size="md" color="dimmed" mt={4}>{ui("homeSubtitle")}</Text>

									<Box className={classes.actionBar} mt={18}>
										{barItems.map((item) => {
											const Icon = item.icon;
											const pill = (
												<Button
													key={item.key}
													size="xs"
													radius="xl"
													variant="default"
													className={classes.actionBarPill}
													leftIcon={<Icon size={14} />}
													onClick={item.menu || item.kind === "more" ? undefined : () => runBarAction(item.key)}
												>
													{item.label}
													{Number.isFinite(item.badge) && item.badge > 0 && (
														<Badge size="xs" ml={6} variant="filled" color="red" radius="xl">{item.badge}</Badge>
													)}
												</Button>
											);
											if (item.key === "create") {
												return (
													<Menu key={item.key} width={220} shadow="md" withinPortal>
														<Menu.Target>{pill}</Menu.Target>
														<Menu.Dropdown>
															{createTypeOptions.map((type) => (
																<Menu.Item key={type.value} onClick={() => seedComposer(`${ui("create")} ${ui(type.labelKey)}: `)}>
																	{ui(type.labelKey)}
																</Menu.Item>
															))}
														</Menu.Dropdown>
													</Menu>
												);
											}
											if (item.key === "analyze") {
												return (
													<Menu key={item.key} width={260} shadow="md" withinPortal>
														<Menu.Target>{pill}</Menu.Target>
														<Menu.Dropdown>
															{ANALYZE_PROMPT_KEYS.map((key) => (
																<Menu.Item key={key} onClick={() => submitPrompt(assistantUiEnglish(key), ui(key))}>
																	{ui(key)}
																</Menu.Item>
															))}
														</Menu.Dropdown>
													</Menu>
												);
											}
											if (item.kind === "more") {
												return (
													<Menu key={item.key} width={220} shadow="md" withinPortal>
														<Menu.Target>{pill}</Menu.Target>
														<Menu.Dropdown>
															<Menu.Item disabled icon={<img src="/assets/AI/icon.png" alt="" width={14} height={14} style={{ borderRadius: "50%", objectFit: "cover" }} />}>
																{assistantBadge.label}
															</Menu.Item>
															{user?.ananunaki === true && (
																<Menu.Item component={Link} to="/system/ai-settings" onClick={onClose} icon={<IconSettings size={14} />}>
																	{ui("aiSettings")}
																</Menu.Item>
															)}
														</Menu.Dropdown>
													</Menu>
												);
											}
											return pill;
										})}
									</Box>

									<Box className={classes.continueGrid} style={{ marginTop: 26 }}>
										{homeCards.map((card) => {
											const Icon = card.icon;
											const cardNode = (
												<Card
													withBorder
													radius="lg"
													p="sm"
													key={card.key}
													className={classes.continueCard}
													onClick={card.menu ? undefined : card.onClick}
												>
													<Group spacing={8} noWrap mb={4}>
														<Box className={classes.chatRowIcon} style={{ background: `var(--mantine-color-${card.color}-light)` }}>
															<Icon size={14} color={`var(--mantine-color-${card.color}-6)`} />
														</Box>
														<Text size="sm" weight={700} lineClamp={1}>{card.title}</Text>
														{Number.isFinite(card.badge) && card.badge > 0 && (
															<Badge size="xs" ml="auto" variant="filled" color="red" radius="xl">{card.badge}</Badge>
														)}
													</Group>
													<Text size="xs" color="dimmed" lineClamp={1}>{card.subtitle}</Text>
												</Card>
											);
											if (!card.menu) return cardNode;
											return (
												<Menu key={card.key} width={220} shadow="md" withinPortal>
													<Menu.Target>{cardNode}</Menu.Target>
													<Menu.Dropdown>
														{card.menu.map((type) => (
															<Menu.Item key={type.value} onClick={() => seedComposer(`${ui("create")} ${ui(type.labelKey)}: `)}>
																{ui(type.labelKey)}
															</Menu.Item>
														))}
													</Menu.Dropdown>
												</Menu>
											);
										})}
									</Box>

									{recentSessions.length > 0 && (
										<>
											<Text size="sm" weight={700} mt={30} mb={10}>{ui("continueChatting")}</Text>
											<Box className={classes.continueGrid}>
												{recentSessions.map((session) => {
													const SessionIcon = sessionIconFor(session.module);
													return (
														<Card
															key={session._id}
															withBorder
															radius="lg"
															p="sm"
															className={classes.continueCard}
															onClick={() => loadSession(session)}
														>
															<Group spacing={8} noWrap mb={4}>
																<Box className={classes.chatRowIcon}>
																	<SessionIcon size={14} color={isDark ? "#93c5fd" : "#2563eb"} />
																</Box>
																<Text size="sm" weight={700} lineClamp={1}>{session.title || ui("newChat")}</Text>
															</Group>
															<Text size="xs" color="dimmed">{formatRelativeTime(session.updatedAt || session.createdAt, lang)}</Text>
														</Card>
													);
												})}
											</Box>
										</>
									)}

									<Text size="sm" weight={700} mt={30} mb={10}>{ui("tryAsking")}</Text>
									<Box className={classes.continueGrid}>
										{TRY_ASKING_PROMPT_KEYS.map((key) => (
											<Card
												key={key}
												withBorder
												radius="lg"
												p="sm"
												className={classes.continueCard}
												onClick={() => submitPrompt(assistantUiEnglish(key), ui(key))}
											>
												<Group spacing={8} noWrap mb={4}>
													<Box className={classes.chatRowIcon}>
														<IconMessageCircle size={14} color={isDark ? "#93c5fd" : "#2563eb"} />
													</Box>
													<Text size="sm" weight={700} lineClamp={2} style={{ flex: 1 }}>{ui(key)}</Text>
													<IconChevronRight size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
												</Group>
											</Card>
										))}
									</Box>
									<Box style={{ height: 8 }} />
								</Box>
							) : (
								<Stack spacing="md" p="md" style={{ maxWidth: 980, margin: "0 auto", minWidth: 0 }}>
									{messages.map((message) => (
										<MessageBubble
											key={message._id}
											message={message}
											onApprove={approve}
											onReject={reject}
											approvalBusy={approvalBusy}
											resolvedApprovalIds={resolvedApprovalIds}
											lang={lang}
											colorScheme={colorScheme}
											agentName={assistantBadge.label}
										/>
									))}
								</Stack>
							)}
						</ScrollArea>
						{loading && !showHome && (
							<Box className={classes.centerLoading} role="status" aria-live="polite">
								<Loader size={64} variant="dots" />
								<Text size="sm" color="dimmed" mt="sm">{ui("thinking")}</Text>
							</Box>
						)}
						</Box>

						<Box className={classes.composerArea}>
							<Box className={classes.composerInner}>

								{isRecording && (
									<Box className={`${drawerClasses.recordingPanel} ${isDark ? drawerClasses.composerDark : drawerClasses.composerLight}`}>
										<Box className={drawerClasses.recordingLive}>
											<Text className={drawerClasses.recordingTime}>{formatRecordingTime(recordingTime)}</Text>
											<LiveRecordingWaveform analyser={recordingAnalyser} paused={isRecordingPaused} />
										</Box>
										<Box className={drawerClasses.recordingActions}>
											<Tooltip label={ui("deleteRecording")}>
												<ActionIcon
													size={46}
													radius="xl"
													variant="light"
													color="red"
													aria-label={ui("deleteRecording")}
													onClick={discardRecording}
													disabled={isRecordingPending}
												>
													<IconTrash size={20} />
												</ActionIcon>
											</Tooltip>
											<button
												type="button"
												className={drawerClasses.recordingPause}
												onClick={togglePauseRecording}
												disabled={isRecordingPending}
											>
												{isRecordingPaused ? <IconMicrophone size={18} /> : <IconPlayerPauseFilled size={18} />}
												<span>{ui(isRecordingPaused ? "resumeRecording" : "pauseRecording")}</span>
											</button>
											<Tooltip label={ui("sendVoiceMessage")}>
												<ActionIcon
													size={46}
													variant="filled"
													radius="xl"
													aria-label={ui("sendVoiceMessage")}
													onClick={() => stopVoiceRecording(true)}
													disabled={loading || isTranscribing || isRecordingPending}
													loading={isRecordingPending}
													className={drawerClasses.sendButton}
													style={{ color: "#fff" }}
												>
													<IconSend size={20} />
												</ActionIcon>
											</Tooltip>
										</Box>
									</Box>
								)}

								<Box
									className={`${drawerClasses.composer} ${isDark ? drawerClasses.composerDark : drawerClasses.composerLight}`}
									style={isRecording ? { display: "none" } : undefined}
								>
									<Menu width={240} shadow="md" position="top-start" withinPortal>
										<Menu.Target>
											<ActionIcon size="lg" variant="subtle" radius="xl" style={{ flexShrink: 0, marginBottom: 2 }}>
												<IconPlus size={18} />
											</ActionIcon>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Label>{ui("addContext")}</Menu.Label>
											{contextOptions.map((opt) => (
												<Menu.Item
													key={opt.value}
													disabled={Boolean(sessionId)}
													icon={selectedContext === opt.value ? <IconCheck size={14} /> : null}
													onClick={() => setSelectedContext(opt.value)}
												>
													{opt.label}
												</Menu.Item>
											))}
											<Menu.Divider />
											<Menu.Item
												icon={<IconPaperclip size={14} />}
												onClick={() => showNotification({ message: ui("attachmentsComingSoon") })}
											>
												{ui("attachFileLabel")}
											</Menu.Item>
										</Menu.Dropdown>
									</Menu>
									<Textarea
										ref={composerRef}
										value={input}
										onChange={(event) => setInput(event.currentTarget.value)}
										minRows={1}
										maxRows={6}
										autosize
										placeholder={ui("askAnythingPlaceholder")}
										disabled={loading}
										onKeyDown={(event) => {
											if (event.key === "Enter" && !event.shiftKey) {
												event.preventDefault();
												sendMessage();
											}
										}}
										styles={{
											root: { flex: 1, minWidth: 0 },
											wrapper: { flex: 1, minWidth: 0 },
											input: {
												background: "transparent",
												resize: "none",
												boxShadow: "none",
												wordBreak: "break-word",
												overflowWrap: "anywhere",
												"&:focus": { outline: "none", boxShadow: "none" },
											},
										}}
									/>
									<Tooltip label={voiceInputTooltip}>
										<ActionIcon
											size="lg"
											color="gray"
											variant="subtle"
											radius="xl"
											aria-label={ui("voiceInput")}
											onClick={startRecording}
											disabled={voiceInputDisabled}
											loading={isTranscribing}
											style={{ flexShrink: 0, marginBottom: 2 }}
										>
											<IconMicrophone size={18} />
										</ActionIcon>
									</Tooltip>
									<ActionIcon
										size="lg"
										variant="filled"
										radius="xl"
										onClick={() => sendMessage()}
										disabled={loading || isTranscribing || !input.trim()}
										className={drawerClasses.sendButton}
										style={{ flexShrink: 0, marginBottom: 2, color: "#fff" }}
									>
										{loading ? <Loader size={16} color="white" /> : <IconSend size={18} />}
									</ActionIcon>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}

			<Modal opened={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} title={ui("renameChatTitle")} size="sm" centered>
				<TextInput
					value={renameValue}
					onChange={(event) => setRenameValue(event.currentTarget.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") submitRename();
					}}
					autoFocus
					mb="md"
				/>
				<Group position="right">
					<Button variant="subtle" onClick={() => setRenameTarget(null)}>{ui("cancel")}</Button>
					<Button onClick={submitRename} loading={sessionActionBusy} disabled={!renameValue.trim()}>{ui("renameChat")}</Button>
				</Group>
			</Modal>

			<Modal opened={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title={ui("deleteChatConfirmTitle")} size="sm" centered>
				<Text size="sm" color="dimmed" mb="md">{ui("deleteChatConfirmBody")}</Text>
				<Group position="right">
					<Button variant="subtle" onClick={() => setDeleteTarget(null)}>{ui("cancel")}</Button>
					<Button color="red" onClick={confirmDeleteSession} loading={sessionActionBusy}>{ui("deleteChat")}</Button>
				</Group>
			</Modal>
		</Modal>
	);
}

export function AssistantDrawerButton({ onClick, disabled }) {
	const { lang } = useLang();
	return (
		<Tooltip label={assistantUiText("aiAssistant", lang)}>
			<ActionIcon variant="light" size="lg" onClick={onClick} disabled={disabled}>
				<IconMessageCircle size={20} />
			</ActionIcon>
		</Tooltip>
	);
}
